import type { QueryClient } from "@tanstack/react-query";

import { getCourseDetailsApiV1CoursesCourseIdGet } from "@/src/api/sdk.gen";
import type { CachedCourseData } from "@/lib/contexts/CourseNavigationContext";

/** React Query key for `/api/courses/[courseId]/core`. */
export const courseCoreQueryKey = (courseId: string) => ["course-core", courseId] as const;

/** React Query key for course certificate/extras data. */
export const courseExtrasQueryKey = (courseId: string) => ["course-extras", courseId] as const;

export type FetchCourseCoreInit = RequestInit & { debugSource?: string };

export async function fetchCourseCore(
    courseId: string,
    init?: FetchCourseCoreInit,
): Promise<CachedCourseData> {
    const debugSource = init?.debugSource ?? "react-query-fetchCourseCore";
    const { data, error, response } = await getCourseDetailsApiV1CoursesCourseIdGet({
        path: { course_id: courseId },
        headers: {
            "X-Debug-Source": debugSource,
        },
    });
    if (response?.status === 404) throw new Error("notFound");
    if (error || !data) throw new Error("error");
    return data as unknown as CachedCourseData;
}

export function invalidateCourseCore(queryClient: QueryClient, courseId: string) {
    return queryClient.invalidateQueries({ queryKey: courseCoreQueryKey(courseId) });
}

/** Keep sessionStorage + React Query aligned after a fresh /core response. */
type CourseNavCacheWriter = {
    setPrefetchedCourseData: (courseId: string, data: CachedCourseData | null) => void;
};

export function applyCourseCoreToCaches(
    queryClient: QueryClient,
    nav: CourseNavCacheWriter | null | undefined,
    routeCourseId: string,
    core: CachedCourseData,
    dataUpdatedAt = Date.now(),
) {
    const merged: CachedCourseData = {
        ...core,
        coreFetchedAt: dataUpdatedAt,
    };
    queryClient.setQueryData(courseCoreQueryKey(routeCourseId), merged);
    nav?.setPrefetchedCourseData(routeCourseId, merged);
    if (merged.id && merged.id !== routeCourseId) {
        queryClient.setQueryData(courseCoreQueryKey(merged.id), merged);
        nav?.setPrefetchedCourseData(merged.id, merged);
    }
    if (merged.uuid && merged.uuid !== routeCourseId) {
        queryClient.setQueryData(courseCoreQueryKey(merged.uuid), merged);
        nav?.setPrefetchedCourseData(merged.uuid, merged);
    }
}

